import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import api from '@/services/api';
import { Product } from '@/types';
import { ProductCard } from '@/components/product/ProductCard';
import { ProductCardSkeleton } from '@/components/common/Skeleton';
import { formatCurrency } from '@/utils';

const COMMON_SYMPTOMS = [
  { label: 'Headache', emoji: '🤕' },
  { label: 'Fever', emoji: '🌡️' },
  { label: 'Cold', emoji: '🤧' },
  { label: 'Cough', emoji: '😮‍💨' },
  { label: 'Allergy', emoji: '🤧' },
  { label: 'Acidity', emoji: '🔥' },
  { label: 'Stomach Pain', emoji: '🤢' },
  { label: 'Muscle Pain', emoji: '💪' },
  { label: 'Skin Rash', emoji: '🩹' },
  { label: 'Diarrhea', emoji: '🚽' },
  { label: 'Constipation', emoji: '😣' },
  { label: 'Anxiety', emoji: '😰' },
  { label: 'Insomnia', emoji: '😴' },
  { label: 'Acne', emoji: '😤' },
  { label: 'Vitamins', emoji: '💊' },
  { label: 'Diabetes', emoji: '🩸' },
];

export const SymptomSearchPage = () => {
  const [selected, setSelected] = useState<string>('');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = async (symptom: string) => {
    setSelected(symptom);
    setLoading(true);
    setSearched(true);
    try {
      const res = await api.get(`/symptoms/search?symptom=${encodeURIComponent(symptom.toLowerCase())}`);
      setProducts(res.data.data || []);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="font-heading font-bold text-2xl text-gray-800 mb-2">Search by Symptom</h1>
        <p className="text-gray-500">Don't know the medicine name? Tell us your symptom — we'll find the right OTC products.</p>
      </div>

      {/* Symptom chips */}
      <div className="flex flex-wrap gap-3 justify-center mb-8">
        {COMMON_SYMPTOMS.map(s => (
          <button
            key={s.label}
            onClick={() => search(s.label)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
              selected === s.label
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 hover:border-primary-300 text-gray-600 hover:bg-primary-50'
            }`}
          >
            <span>{s.emoji}</span> {s.label}
          </button>
        ))}
      </div>

      {/* Results */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array(8).fill(0).map((_, i) => <ProductCardSkeleton key={i} />)}
        </div>
      ) : searched && products.length === 0 ? (
        <div className="text-center py-12">
          <Search className="h-12 w-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500">No OTC products found for "{selected}".</p>
          <p className="text-sm text-gray-400 mt-1">This may require a prescription — please consult our pharmacist.</p>
        </div>
      ) : products.length > 0 ? (
        <>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-gray-800">
              {products.length} products for <span className="text-primary-600">"{selected}"</span>
            </h2>
            <p className="text-xs text-gray-400">Showing OTC medicines only</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-gray-400">
          <span className="text-4xl mb-4 block">👆</span>
          <p>Select a symptom above to find relevant medicines</p>
        </div>
      )}
    </div>
  );
};
